import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { KnowledgeBaseActionDto } from '@/Domain/api/Action/dto/KnowledgeBaseAction.dto';
import { useModalStore } from '@/Domain/store/modal.store';

const knowledgeBaseActionSchema = (isActive: boolean) => {
  if (isActive) {
    return z.object({
      name: z.string().min(1).max(100),
      description: z.string().min(1).max(1000),
      parameter_description: z.string().min(1).max(500),
      tag: z.string(),
    });
  }
  return z.object({
    name: z.string().max(100),
    description: z.string().max(1000),
    parameter_description: z.string().max(500),
    tag: z.string(),
  });
};

export type KnowledgeBaseActionForm = z.infer<ReturnType<typeof knowledgeBaseActionSchema>>;

export const useKnowledgeBaseActionForm = (knowledgeBaseAction?: KnowledgeBaseActionDto) => {
  const { t } = useTranslation('knowledge_base_action');
  const queryClient = useQueryClient();
  const { open, closeModal } = useModalStore();

  useEffect(() => {
    if (!knowledgeBaseAction) return;
  }, [knowledgeBaseAction]);

  const patchKnowledgeBaseActionMutation = useMutation<
    KnowledgeBaseActionDto,
    unknown,
    Partial<KnowledgeBaseActionDto>
  >({
    mutationKey: [QueryKey.actions.knowledgeBase.update, knowledgeBaseAction?.id],
    onSuccess: (data) => {
      closeModal();

      queryClient.setQueryData(
        [QueryKey.actions.knowledgeBase.get, knowledgeBaseAction?.id],
        () => ({
          ...data,
        }),
      );

      void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.list] });
      toast.success(t('toast.update_success'));
    },
    onError: () => {
      toast.error(t('toast.update_failed'), {
        duration: 5000,
        description: t('toast.update_failed'),
      });
    },
  });

  const form = useForm<KnowledgeBaseActionForm>({
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      name: knowledgeBaseAction?.name || '',
      description: knowledgeBaseAction?.description || '',
      parameter_description: knowledgeBaseAction?.parameter_description || '',
      tag: knowledgeBaseAction?.tag || '',
    },
    resolver: zodResolver(knowledgeBaseActionSchema(!!knowledgeBaseAction?.is_active)),
  });

  const handleSubmit = async (data: KnowledgeBaseActionForm) => {
    if (!knowledgeBaseAction) return;
    const updateData = {
      id: knowledgeBaseAction.id,
      name: data.name,
      description: data.description,
      parameter_description: data.parameter_description,
      tag: data.tag,
    };
    patchKnowledgeBaseActionMutation.mutate(updateData);
  };

  useEffect(() => {
    if (open || knowledgeBaseAction) {
      form.reset({
        name: knowledgeBaseAction?.name || '',
        description: knowledgeBaseAction?.description || '',
        parameter_description: knowledgeBaseAction?.parameter_description || '',
        tag: knowledgeBaseAction?.tag || '',
      });
    }
  }, [open, knowledgeBaseAction]);

  const onInvalid = () => {
    toast.error(t('toast.invalid_update_on_is_active'));
  };

  return { form, handleSubmit, onInvalid };
};
